import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalError from "../../components/NtModalError";
import NtModalInputField from "../../components/NtModalInputField";
import NtToggleRow from "../../components/NtToggleRow";
import NtModal from "../../components/NtModal";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import NtModalAccountPickerField from "./NtModalAccountPickerField";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import { showToast } from "../../common/utils/ControllerUtils";
import { releaseNumber } from "../../api/number";
import {
  fetchInboundRoutes,
  createInboundRoute,
  updateInboundRoute,
  deleteInboundRoute,
} from "../../api/inboundroute";
import { formatTelephoneNumber, digits10 } from "../utils/numberUtils";

// dialplan_enabled comes back boolean-ish depending on the endpoint — coerce.
function asBool(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return fallback;
}

/**
 * Manage a single DID: set its call routing (the FusionPBX inbound route that
 * maps the number → an extension / ring group / IVR / …) or release it.
 * Numbers themselves aren't editable through the ntconnect API — routing lives
 * on the PBX side, release on the ntconnect side.
 */
function NtNumberManageForm({ item, onCancel, onUpdate, onDelete }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const context = useContext(UserContext);
  const domain = context?.user?.domain;
  const confirmRef = useRef();

  const tn = item?.did;

  const [action, setAction] = useState(null); // picker value {name,value,item}
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [route, setRoute] = useState(null); // existing { id, name, number }
  const [routeLoading, setRouteLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState(null);

  const loadRoute = async () => {
    setRouteLoading(true);
    try {
      const res = await fetchInboundRoutes(domain, 1, 1000, tn);
      const routes = res?.ok ? res.data?.data || [] : [];
      const target = digits10(tn);
      const match = routes.find((r) => digits10(r.dialplan_number) === target);
      if (match) {
        setRoute({ id: match.dialplan_uuid, name: match.dialplan_name, number: match.dialplan_number });
        setAction(match.dialplan_destination_actions || null);
        setDescription(match.dialplan_description || "");
        setEnabled(asBool(match.dialplan_enabled, true));
      } else {
        setRoute(null);
        setDescription("");
        setEnabled(true);
      }
    } catch (e) {
      Logger("Number routing · load failed", e);
    } finally {
      setRouteLoading(false);
    }
  };

  useEffect(() => {
    if (domain && tn) loadRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, tn]);

  const handleSaveRouting = async () => {
    // No destination selected — if a route exists, clearing it means "remove
    // routing" (delete the inbound route). Otherwise there's nothing to do.
    if (!action) {
      if (route?.id) handleRemoveRouting();
      return;
    }
    setSaving(true);
    setError(null);
    const params = {
      name: route?.name || `DID ${tn}`,
      destination: route?.number || tn,
      action,
      description: description.trim() || undefined,
      enabled,
    };
    const res = route?.id
      ? await updateInboundRoute(domain, params, route.id)
      : await createInboundRoute(domain, params);
    Logger("Number routing · save", res);
    setSaving(false);
    if (res.ok) {
      showToast("Routing saved", `Calls to ${formatTelephoneNumber(tn)} will follow the destination.`, "success");
      onUpdate?.();
    } else {
      setError(parseError(res, "Couldn't save routing. Please try again."));
    }
  };

  const handleRemoveRouting = async () => {
    setSaving(true);
    setError(null);
    const res = await deleteInboundRoute(domain, route.id);
    Logger("Number routing · remove", res);
    setSaving(false);
    if (res.ok) {
      showToast("Routing removed", `${formatTelephoneNumber(tn)} is no longer routed.`, "success");
      onUpdate?.();
    } else {
      setError(parseError(res, "Couldn't remove routing. Please try again."));
    }
  };

  const handleRelease = async () => {
    setReleasing(true);
    setError(null);
    const res = await releaseNumber(tn);
    Logger("Number release", res);
    setReleasing(false);
    if (res.ok) onDelete?.();
    else setError(parseError(res, "Couldn't release this number. Please try again."));
  };

  const busy = saving || releasing;

  return (
    <View style={styles.container}>
      <NtModalTitle>Manage number</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Route calls to a destination, or release the number.
      </NtModalSubtitle>

      <View style={styles.headerRow}>
        <NtText style={styles.number}>{formatTelephoneNumber(tn)}</NtText>
      </View>

      {/* Call routing */}
      {routeLoading ? (
        <View style={styles.loadingRow}>
          <NtActivityIndicator color={theme.brand2} size="small" />
          <NtText style={styles.loadingText}>Checking current routing…</NtText>
        </View>
      ) : (
        <>
          <NtModalAccountPickerField
            containerStyle={{ marginTop: 18 }}
            title="Route calls to"
            placeholder="Select an extension, ring group, or voicemail…"
            value={action}
            setValue={setAction}
            // The inbound-route API only accepts extension / ringgroup /
            // voicemail (+ hangup); hide recording/other so picks can't 422.
            removeOptions={["recording", "other"]}
          />

          <NtModalInputField
            containerStyle={{ marginTop: 15 }}
            title="Description"
            placeholder="Optional — e.g. “Main line”, “Sales DID”"
            value={description}
            setValue={setDescription}
          />

          {action ? (
            <View style={{ marginTop: 16 }}>
              <NtToggleRow
                value={enabled}
                onChange={setEnabled}
                label="Enable routing"
                sub="When off, the route stays saved but calls won't follow it."
              />
            </View>
          ) : null}

          <NtText style={styles.status}>
            {!action && route?.id
              ? "Destination cleared — press Update to remove routing from this number."
              : route?.id
                ? "This number is routed. Change the destination and press Update."
                : "Not routed yet — pick a destination to connect this number."}
          </NtText>
        </>
      )}

      <NtModalError error={error} containerStyle={{ marginTop: 10 }} />

      <View style={styles.footer}>
        <NtModalActiveButton
          containerStyle={{ backgroundColor: theme.red }}
          title="Release"
          icon="delete"
          onPress={() => confirmRef.current?.show()}
          enabled={!busy}
        />

        <View style={styles.footerRight}>
          {busy && (
            <NtActivityIndicator color={theme.brand2} size="small" containerStyle={{ marginRight: 18 }} />
          )}
          <NtModalUnactiveButton title="Cancel" onPress={onCancel} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title="Update"
            onPress={handleSaveRouting}
            enabled={(!!action || !!route?.id) && !busy}
          />
        </View>
      </View>

      <NtModal ref={confirmRef}>
        <NtModalConfirmAlert
          title="Release number"
          message={`Release ${formatTelephoneNumber(tn)}? It will be removed from your account and can't be recovered.`}
          onCancel={() => confirmRef.current?.dismiss()}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleRelease();
          }}
        />
      </NtModal>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { flex: 1, minWidth: 480, maxWidth: 560, padding: 15 },
    headerRow: { flexDirection: "row", alignItems: "center", gap: 12, marginTop: 14 },
    number: { fontFamily: t.fontMono || "Menlo", fontSize: 17, fontWeight: "700", color: t.text, letterSpacing: 0.3 },
    loadingRow: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 18 },
    loadingText: { color: t.textDim, fontSize: 12.5 },
    status: { color: t.textMute, fontSize: 11, marginTop: 8, fontStyle: "italic" },
    footer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 24,
    },
    footerRight: { flexDirection: "row", alignItems: "center" },
  });

export default NtNumberManageForm;
